import { Box, Typography, useTheme } from '@mui/material'
import { CheckBoxIcon, CheckedBlueBoxIcon, Icon } from 'packages/eid-icons'
import { Pagination, ToggleSwitch, ThresholdSearchInput } from 'packages/eid-ui'
import Search from 'packages/eid-ui/GenericHeader/HeaderSearch'
import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_PAGE_SIZE_DETAILS, paginate, useDebounce } from 'packages/core'
import RequiredLabel from '../Components/FieldItemWrapper/RequiredLabel'
import RequiredFieldErrorMessage from '../Components/FieldItemWrapper/RequiredFieldErrorMessage'
import { Skeleton, Checkbox } from '@mui/material'
import CheckboxListWithInfoIcon from '../Components/CheckBoxListWithInfoIcon'
import { getFieldTypeRequiredErrorMessage } from 'packages/core'
import InfoMessageForWrongJson from '../Components/InfoMessageForWrongJson'
import styled from '@emotion/styled'
import InfoBoxMessage from 'packages/eid-ui/InfoBoxMessage'
import { getInfoTooltipFieldsConfig } from 'packages/core'
import config from 'config'

const RootContainer = styled(Box)({
    background: '#fbfbfd',
    margin: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.6rem',
})
const HeaderBox = styled(Box)({
    display: 'flex',
    marginTop: '2rem',
    padding: '0 3rem',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& h3': {
        fontSize: '1.6rem',
        fontWeight: '600',
    },
    '& p': {
        fontSize: '1.2rem',
        color: '#5d6870',
    },
})

const ToggleBox = styled(Box)({
    display: 'flex',
    padding: '0px 3rem',
    alignItems: 'center',
    '& h3': {
        fontSize: '1.6rem',
        fontWeight: '600',
    },
    '& p': {
        fontSize: '1.2rem',
        color: '#5d6870',
    },
})

const PaginationSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center',
    padding: '2.4rem 1.1rem',
    boxShadow: '0 .4rem .6rem -0.2rem rgba(0, 0, 0, 0.1)',
    '& .MuiSelect-select': {
        border: 'none',
        boxShadow: 'none',
    },
    '& .MuiInputBase-root': {
        border: 'solid 0.1rem #ebebed',
        boxShadow: '0 .4rem .6rem -0.2rem rgba(0, 0, 0, 0.1)',
        borderRadius: '.5rem !important',
        padding: '0 .6rem',
        background: theme?.palette?.background?.paper,
    },
}))

const MultiSelectCheckBoxList = ({
    fieldType,
    isFormValidated,
    valueType,
    isApiSupportedSource,
    useDataHook,
    handleUpdate,
}) => {
    const { t } = useTranslation()
    const [sourceValues, setSourceValues] = useState([])
    const [showFieldValues, setShowFieldValues] = useState(true)
    const [searchKey, setSearchKey] = useState('')
    const debouncedValue = useDebounce(searchKey)

    const theme = useTheme()
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_DETAILS)
    const [errorMessage, setErrorMessage] = useState('')
    const [page, setPage] = useState(1)
    const [sourceUpdated, setSourceUpdated] = useState(false)
    const hideFieldTypeSelectAllToggle = config.HIDE_FIELD_TYPE_SELECT_ALL_TOGGLE=='true' || config.HIDE_FIELD_TYPE_SELECT_ALL_TOGGLE==true
    let minimumRowsPerPage

    useEffect(() => {
        if (fieldType?.SourceValues && sourceUpdated) {
            setShowFieldValues(
                fieldType?.SourceValues?.length || searchKey ? true : false,
            )
            setSourceValues(fieldType?.SourceValues || [])
        }
    }, [fieldType?.SourceValues, searchKey, sourceUpdated])

    useEffect(() => {
        if (isFormValidated && !fieldType?.[valueType]?.length) {
            setErrorMessage(getFieldTypeRequiredErrorMessage(fieldType))
        } else {
            setErrorMessage('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isFormValidated,
        fieldType?.[valueType],
        fieldType?.AssignedAllValuesRange,
    ])

    const { data: sourceData, isFetching } = useDataHook(
        fieldType,
        (page - 1) * pageSize,
        pageSize,
        debouncedValue,
    )

    const getFieldSourceData = () => {
        if (!sourceValues?.length) return []
        const configuredFields =
            getInfoTooltipFieldsConfig().map(({ field }) => field) || []
        return sourceValues.map((item) => {
            const base = {
                id: item.ValueName,
                friendlyName: item.FriendlyName,
                description: item.Description,
            }
            configuredFields.forEach((field) => {
                if (item[field] != null) base[field] = item[field]
            })
            return base
        })
    }

    useEffect(() => {
        if (isApiSupportedSource) {
            if (!isFetching) {
                updateSourceValues()
                setSourceUpdated(true)
            }
        } else {
            setShowFieldValues(
                fieldType?.SourceValues?.length || searchKey ? true : false,
            )
            setSourceValues(fieldType?.SourceValues || [])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isApiSupportedSource, isFetching, sourceData])

    const updateSourceValues = () => {
        const valueToUpdate = {
            ...fieldType,
            SourceValues: sourceData?.data || [],
            totalCount: sourceData?.totalCount,
        }
        handleUpdate(valueToUpdate)
    }

    const onChange = (item) => {
        const tempAssignedValues = fieldType?.[valueType] || []
        const currentIndex = tempAssignedValues.findIndex(
            (i) => i.ValueName === item.id,
        )
        const newChecked = [...tempAssignedValues]

        if (currentIndex === -1) {
            const configFields = {}
            const tempConfigFields = getInfoTooltipFieldsConfig()
            tempConfigFields.forEach(({ field }) => {
                if (item[field] != null) configFields[field] = item[field]
            })
            newChecked.push({
                FriendlyName: item.friendlyName,
                ValueName: item.id,
                ...configFields,
            })
        } else {
            newChecked.splice(currentIndex, 1)
        }

        const valueToUpdate = {
            ...fieldType,
            [valueType]: newChecked,
        }

        handleUpdate(valueToUpdate)
    }

    const handlePageChange = (_, value) => {
        setPage(value)
    }

    const handleSearch = (val) => {
        setSearchKey(val)
        setPage(1)
    }

    if (DEFAULT_PAGE_SIZE_DETAILS < 10) {
        minimumRowsPerPage = DEFAULT_PAGE_SIZE_DETAILS
    }

    const getPaginatedItems = () => {
        if (isApiSupportedSource) {
            return {
                items: getFieldSourceData(),
                totalCount: fieldType?.totalCount,
            }
        }
        const filtered = getFieldSourceData().filter((item) =>
            item?.friendlyName?.toLowerCase().includes(searchKey.toLowerCase()),
        )
        return {
            items: paginate(filtered, page, pageSize),
            totalCount: filtered.length,
        }
    }

    const isAllOnPageSelected = () => {
        try {
            const tempAssignedValues = fieldType?.[valueType] || []
            const currentPageItems = getPaginatedItems().items || []
            if (currentPageItems?.length && tempAssignedValues?.length) {
                return currentPageItems.every((item1) =>
                    tempAssignedValues.some(
                        (item2) => item2.ValueName === item1.id,
                    ),
                )
            }
        } catch (err) {}
        return false
    }

    const handleSelectAllOnPage = (e) => {
        try {
            const isChecked = e.target.checked
            const currentPageItems = getPaginatedItems().items || []
            const tempAssignedValues = fieldType?.[valueType] || []
            const newChecked = [...tempAssignedValues]
            currentPageItems.forEach((item) => {
                if (isChecked) {
                    const currentIndex = tempAssignedValues.findIndex(
                        (i) => i.ValueName === item.id,
                    )
                    if (currentIndex === -1) {
                        const configFields = {}
                        const tempConfigFields = getInfoTooltipFieldsConfig()
                        tempConfigFields.forEach(({ field }) => {
                            if (item[field] != null)
                                configFields[field] = item[field]
                        })
                        newChecked.push({
                            FriendlyName: item.friendlyName,
                            ValueName: item.id,
                            ...configFields,
                        })
                    }
                } else {
                    const selectedIndex = newChecked.findIndex(
                        (i) => i.ValueName === item.id,
                    )
                    if (selectedIndex !== -1) {
                        newChecked.splice(selectedIndex, 1)
                    }
                }
            })

            const valueToUpdate = {
                ...fieldType,
                [valueType]: newChecked,
            }

            handleUpdate(valueToUpdate)
        } catch (err) {}
    }

    const toggleSelectAll = () => {
        const valueToUpdate = {
            ...fieldType,
            AssignedAllValuesRange: !fieldType.AssignedAllValuesRange,
        }
        handleUpdate(valueToUpdate)
    }

    return (
        <RootContainer>
            <Box>
                <HeaderBox>
                    <Box
                        display="flex"
                        alignItems="flex-start"
                        style={{
                            gap: '1.5rem',
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    width: !showFieldValues
                                        ? '23.2rem'
                                        : 'auto',
                                }}
                                variant="h3"
                            >
                                {fieldType?.Name}{' '}
                                {fieldType?.FieldTypeScope && (
                                    <span>
                                        ({fieldType?.FieldTypeScope || '-'})
                                    </span>
                                )}
                            </Typography>
                            {fieldType?.IsRequired && <RequiredLabel />}
                        </Box>

                        {showFieldValues ? (
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{
                                    gap: '0.8rem',
                                }}
                            >
                                {fieldType?.[valueType]?.length ||
                                fieldType?.AssignedAllValuesRange ? (
                                    <Icon
                                        name="Check"
                                        color={theme?.palette?.success?.main}
                                        width="1.2rem"
                                        height="1.4rem"
                                    />
                                ) : null}
                                <Typography
                                    sx={{
                                        color:
                                            fieldType?.[valueType]?.length ||
                                            fieldType?.AssignedAllValuesRange
                                                ? `${theme?.palette?.success?.main} !important`
                                                : ``,
                                    }}
                                >{`${
                                    fieldType?.AssignedAllValuesRange
                                        ? 'All'
                                        : fieldType?.[valueType]?.length || 0
                                } ${t('Selected')}`}</Typography>
                            </Box>
                        ) : (
                            <Box>
                                <InfoMessageForWrongJson />
                            </Box>
                        )}
                    </Box>

                    {showFieldValues && !fieldType?.AssignedAllValuesRange && (
                        <Box
                            sx={{
                                boxShadow:
                                    '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
                                border: 'solid 0.1rem #ebebed',
                                borderRadius: '0.4rem',
                                maxWidth: '24rem',
                                width: '100%',

                                '&:hover, &:focus': {
                                    borderColor: theme?.palette?.primary?.main,
                                },

                                '& input': {
                                    fontSize: '1.5rem',
                                },
                                '& svg': {
                                    color: '#aab0b4',
                                    width: '1.8rem !important',
                                    height: '1.8rem !important',
                                },
                                ...(fieldType?.IsReadOnly && {
                                    pointerEvents: 'none',
                                    opacity: '.6',
                                    cursor: 'not-allowed !important',
                                }),
                            }}
                        >
                            <ThresholdSearchInput
                                styleData={{}}
                                value={searchKey}
                                handleSearch={(val) => handleSearch(val)}
                                placeholder={t('QuickSearch')}
                            />
                        </Box>
                    )}
                </HeaderBox>

                {showFieldValues && !hideFieldTypeSelectAllToggle && (
                    <ToggleBox>
                        <div
                            style={{
                                marginRight: '8px',
                                paddingTop: '2px',
                                color: fieldType?.AssignedAllValuesRange
                                    ? '#8b909a'
                                    : '#000000',
                            }}
                        >
                            {t('Common_ExplicitlySelectValues')}
                        </div>
                        <ToggleSwitch
                            onChange={toggleSelectAll}
                            value={fieldType?.AssignedAllValuesRange}
                            label={t('Common_SelectAllValues')}
                            labelColor={
                                fieldType?.AssignedAllValuesRange
                                    ? '#000000'
                                    : '#8b909a'
                            }
                            sx={{
                                '& .MuiSwitch-track': {
                                    backgroundColor:
                                        theme?.palette?.primary?.main,
                                },
                            }}
                        />
                    </ToggleBox>
                )}
            </Box>

            <Box
                sx={{
                    padding: '1.4rem 3rem 0',
                    '& .MuiList-root': {
                        columnCount: '2',
                        columnGap: '1.5rem',
                    },
                    '& .MuiListItem-root': {
                        padding: '1.8rem 0.625rem',
                        borderBottom: '0.1rem solid #efeff1',
                        margin: '0',
                        borderTop: '0.1rem solid #efeff1',
                    },
                    '& p': {
                        fontSize: '1.3rem !important',
                        fontWeight: '500',
                        color: '#3b454d !important',
                    },

                    ...(fieldType?.IsReadOnly && {
                        pointerEvents: 'none',
                        opacity: '.6',
                        cursor: 'not-allowed !important',
                    }),
                }}
            >
                {/* Types List */}
                {isFetching ? (
                    <Fragment>
                        <Skeleton height={40} />
                        <Skeleton height={40} />
                        <Skeleton height={40} />
                    </Fragment>
                ) : showFieldValues && fieldType?.AssignedAllValuesRange ? (
                    <InfoBoxMessage
                        message={t('Common_AllPossibleValuesSelected')}
                    />
                ) : showFieldValues ? (
                    <Box>
                        {getPaginatedItems().items?.length ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    '& .MuiCheckbox-root': {
                                        marginLeft: '-0.2rem',
                                    },
                                }}
                            >
                                <Checkbox
                                    edge="start"
                                    color="primary"
                                    icon={
                                        <Box display="flex" alignItems="center">
                                            <CheckBoxIcon />
                                        </Box>
                                    }
                                    checkedIcon={
                                        <Box display="flex" alignItems="center">
                                            <CheckedBlueBoxIcon />
                                        </Box>
                                    }
                                    checked={isAllOnPageSelected()}
                                    onChange={(e) => handleSelectAllOnPage(e)}
                                />
                                <Typography
                                    variant="span"
                                    sx={{
                                        fontSize: '1.3rem',
                                        wordBreak: 'break-word',
                                        wordWrap: 'break-word',
                                        overflow: 'auto',
                                        fontWeight: '700',
                                        marginLeft: '.4rem',
                                    }}
                                >
                                    {t('Common_SelectAllOnPage')}
                                </Typography>
                            </Box>
                        ) : null}
                        <Box>
                            <CheckboxListWithInfoIcon
                                dataList={getPaginatedItems().items || []}
                                fontColor={theme?.palette?.common?.black}
                                handleToggle={onChange}
                                checked={
                                    fieldType?.[valueType]?.map((item) => {
                                        return {
                                            id: item.ValueName,
                                            friendlyName: item.FriendlyName,
                                        }
                                    }) || []
                                }
                                highlightText={searchKey}
                                fallbackMessage={t('Common_NoDataFound')}
                                highlightSelected={true}
                                showInfoTooltip={true}
                            />
                        </Box>
                    </Box>
                ) : null}
            </Box>
            {Boolean(errorMessage) && (
                <Box>
                    <RequiredFieldErrorMessage
                        errorMsgKey={errorMessage}
                        sx={{
                            paddingLeft: '4rem !important',
                            marginTop: '-1.2rem',
                        }}
                    />
                </Box>
            )}
            {showFieldValues && (
                <Box>
                    <PaginationSection>
                        {!fieldType?.AssignedAllValuesRange && (
                            <Pagination
                                count={
                                    Math.ceil(
                                        getPaginatedItems()?.totalCount
                                            ? getPaginatedItems()?.totalCount /
                                                  pageSize
                                            : 0,
                                    ) || 0
                                }
                                size="large"
                                siblingCount={0}
                                page={page}
                                onChange={handlePageChange}
                                showItemsPerPageSelection={true}
                                itemsPerPage={pageSize}
                                onPageSelection={(gotoPage) =>
                                    setPage(gotoPage)
                                }
                                onItemsPerPageChange={(perPage) => {
                                    setPage(1)
                                    setPageSize(perPage)
                                }}
                                showPageSelection={true}
                                minimumRowsPerPage={minimumRowsPerPage}
                                totalCount={getPaginatedItems().totalCount || 0}
                                showCountStat={true}
                            />
                        )}
                    </PaginationSection>
                </Box>
            )}
        </RootContainer>
    )
}

export default MultiSelectCheckBoxList
